import React, { useEffect, useMemo } from 'react';
import ShowRecordsSection from '../../components/investigation/show/single/show_records_section';
import AddAddendumModal from '../../components/investigation/show/single/AddAddendumModal';
import AddendaList from '../../components/investigation/show/single/AddendaList';
import { filterRecordsFromSection } from '../../utils';
import { ISurvey } from '../../constants/types';
import Loader from '../../components/Loader';
import { getSubmissionPatientService, getSubmissionEditHistoryService } from '../../services';
import { useDispatch, useSelector } from 'react-redux';
import { get, isNumber } from 'lodash';
import { MatrixForm } from './patient/Forms/Matrix';
import { updateSubmissionPatientAction, fetchSurveySubmissionsPatientAction, addAddendumPatientAction } from '../../redux/actions/submissionsPatientActions';
import { useHistory } from 'react-router-dom';
import { HOSPITAL_PATIENT } from '../../routes/urls';
import { Button, TextField } from '@mui/material';
import { Translate } from 'react-localize-redux';
import Modal from '../../components/general/modal';
import CryptoJS from 'crypto-js';
import { PERMISSION } from '../../components/investigation/share/user_roles';
import { isSlimSurvey } from '../../utils/slimSubmissions';
import AmendmentHistory, { AmendmentEntry } from './AmendmentHistory';
import { isEditReasonRequiredError } from '../../utils/submissionUpdateErrors';

interface ShowSubmissionPatientProps {
    currentSurvey:ISurvey,
    forceEdit:boolean,
    printMode?:boolean,
    submission?:any,
    idSubmission?:number,
    callBackEditSubmission?: (idSubmission:number, uuidSection:string, submission:any) => void,
    /** SHER-680: called with the uuid of the section whose printer icon was clicked. */
    onPrintSubmission?: (uuidSection: string) => void,
}

const ShowSingleSubmissionPatient: React.FC<ShowSubmissionPatientProps> = ({ currentSurvey, forceEdit, printMode, submission, idSubmission, callBackEditSubmission, onPrintSubmission }) => {
    const uuidInvestigation = useSelector((state:any) => state.investigations.currentInvestigation.uuid);
    const patientsSubmissions = useSelector((state:any) => state.patientsSubmissions.data);
    
    const [submisionLocal, setSubmissionLocal] = React.useState<any>(null);
    const [loading, setLoading] = React.useState(isNumber(idSubmission));
    
    const submissionCached = useMemo(() => {
        if(submission){
            return submission;
        }
        return submisionLocal;
    }, [submission, submisionLocal]);


    const allSubmissions = useMemo(() => {
        if (!submissionCached?.uuidPatient || !patientsSubmissions?.[submissionCached.uuidPatient]) {
            return [];
        }
        
        const patientData = patientsSubmissions[submissionCached.uuidPatient];
        const surveyData = patientData[currentSurvey.uuid];
        
        if (!surveyData?.submissions) {
            return [];
        }
        
        return surveyData.submissions;
    }, [submissionCached?.uuidPatient, patientsSubmissions, currentSurvey.uuid]);

    const history = useHistory();
    const dispatch = useDispatch();

    const [showEditReasonModal, setShowEditReasonModal] = React.useState(false);
    const [editReason, setEditReason] = React.useState('');
    const [editPassword, setEditPassword] = React.useState('');
    const [editPasswordError, setEditPasswordError] = React.useState('');
    const [editReasonError, setEditReasonError] = React.useState('');
    const [pendingEditData, setPendingEditData] = React.useState<{postObj: any, submissionId?: number} | null>(null);

    const FORTY_EIGHT_HOURS = 48 * 60 * 60 * 1000;

    function isSubmissionOlderThan48h(): boolean {
        if (!submissionCached?.createdAt) return false;
        const createdAt = new Date(submissionCached.createdAt).getTime();
        return (Date.now() - createdAt) > FORTY_EIGHT_HOURS;
    }

    const requiresPassword = isSubmissionOlderThan48h();

    async function confirmEditWithReason() {
        if (!editReason.trim()) {
            setEditReasonError('Edit reason is required');
            return;
        }
        setEditReasonError('');
        if (requiresPassword && !editPassword) {
            setEditPasswordError('Password is required');
            return;
        }
        setEditPasswordError('');
        if (pendingEditData) {
            const { postObj, submissionId } = pendingEditData;
            postObj.editReason = editReason.trim();
            if (requiresPassword) {
                postObj.password = CryptoJS.SHA256(editPassword).toString(CryptoJS.enc.Base64);
            }
            setShowEditReasonModal(false);
            setEditReason('');
            setEditPassword('');
            setEditPasswordError('');
            setEditReasonError('');
            setPendingEditData(null);
            try {
                await dispatch(updateSubmissionPatientAction(postObj, uuidInvestigation, submissionCached?.uuidPatient, currentSurvey.uuid, currentSurvey.name, submissionId));
            } catch (error: any) {
                const errorCode = error.errorCode;
                // SHER-782: the API refuses an edit without a reason (cross-author edits
                // always, self-edits past the grace window). Without this branch the
                // refusal fell through to the alert() below and the clinician had no way
                // to give the reason the server was asking for — the edit was simply lost.
                if (isEditReasonRequiredError(error)) {
                    setPendingEditData({ postObj, submissionId });
                    setShowEditReasonModal(true);
                    return;
                }
                if (errorCode === 'PASSWORD_REQUIRED' || errorCode === 'INVALID_PASSWORD') {
                    setPendingEditData({ postObj, submissionId });
                    setEditReason(postObj.editReason || '');
                    setShowEditReasonModal(true);
                    setEditPasswordError(errorCode === 'INVALID_PASSWORD' ? 'Incorrect password' : 'Password is required');
                    return;
                }
                const errorMessage = error.message || error.errorMessage || 'Failed to update submission';
                console.error('Error updating submission:', errorMessage, error);
                alert(errorMessage);
            }
        }
    }

    function closeEditReasonModal() {
        setShowEditReasonModal(false);
        setEditReason('');
        setEditPassword('');
        setEditPasswordError('');
        setEditReasonError('');
        setPendingEditData(null);
    }

    console.log("currentSurvey beginning", currentSurvey);
    console.log("submission beginning", submission);
    console.log("submissionCached beginning", submissionCached);
    console.log("allSubmissions beginning", allSubmissions);

    useEffect(() => {
        async function getSubmission() {
            setLoading(true);
            const sub = await getSubmissionPatientService(uuidInvestigation, idSubmission, false);
            setSubmissionLocal(sub.submission);
            setLoading(false);
        }
        if(idSubmission && !submission){
            getSubmission();   
        }
        else{
            setLoading(false);
        }    
    }, [idSubmission, submission]);
    
    // SHER-492/SHER-495: the MATRIX FORM view compares all submissions OF THE CURRENT SURVEY
    // (allSubmissions reads patientsSubmissions[uuid][currentSurvey.uuid] only). With the slim
    // list the store holds metadata-only rows, so we load the full payload when this survey's
    // bucket is missing or still slim — but per-survey now, not the whole patient. Normal
    // submission views get their content from the on-demand single fetch in show_patient_records.
    useEffect(() => {
        if (currentSurvey?.name !== "MATRIX FORM" || !submissionCached?.uuidPatient) {
            return;
        }
        const surveyData = patientsSubmissions?.[submissionCached.uuidPatient]?.[currentSurvey.uuid];
        const isSlimOrMissing = !surveyData?.submissions || isSlimSurvey(surveyData);
        if (isSlimOrMissing) {
            dispatch(fetchSurveySubmissionsPatientAction(uuidInvestigation, submissionCached.uuidPatient, currentSurvey.uuid));
        }
    }, [currentSurvey, submissionCached?.uuidPatient, uuidInvestigation, dispatch, patientsSubmissions]);


    if(loading){
        return <Loader />
    }

    console.log(currentSurvey);
    console.log(submission);


    if (currentSurvey.name === "MATRIX FORM") {
        console.log("submissionCached:", submissionCached);
        console.log("submissionCached.surveyRecords:", submissionCached?.surveyRecords);
        console.log("allSubmissions for MatrixForm:", allSubmissions);

        return <>
            <MatrixForm survey={currentSurvey}
                submissions={allSubmissions}
                isVisualizingMode={true}
                callbackSurveyCompleted={
                    (values: any, submissionId?: number) => {
                        const postObj = { uuidSurvey: currentSurvey.uuid, uuid_patient: submissionCached?.uuidPatient, id: idSubmission, surveyRecords: values };
                        setPendingEditData({ postObj, submissionId });
                        setShowEditReasonModal(true);
                }
            }  />
            <Modal
                open={showEditReasonModal}
                closeModal={closeEditReasonModal}
                title={<Translate id="hospital.submission-edit-reason-title" />}
                confirmAction={confirmEditWithReason}
                confirmButtonLabel="hospital.submission-edit-confirm"
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%', minWidth: '300px' }}>
                    <TextField
                        label={<Translate id="hospital.submission-edit-reason-label" />}
                        value={editReason}
                        onChange={(e) => { setEditReason(e.target.value); setEditReasonError(''); }}
                        multiline
                        rows={3}
                        fullWidth
                        required
                        error={!!editReasonError}
                        helperText={editReasonError}
                        autoFocus
                    />
                    {requiresPassword && (
                        <TextField
                            label={<Translate id="hospital.submission-edit-password-label" />}
                            type="password"
                            value={editPassword}
                            onChange={(e) => { setEditPassword(e.target.value); setEditPasswordError(''); }}
                            fullWidth
                            required
                            error={!!editPasswordError}
                            helperText={editPasswordError || <Translate id="hospital.submission-edit-password-required" />}
                        />
                    )}
                </div>
            </Modal>
        </>
    }


    return <ShowSingleSubmissionPatientView
                currentSurvey={currentSurvey}
                forceEdit={forceEdit}
                printMode={printMode}
                submission={submissionCached}
                uuidInvestigation={uuidInvestigation}
                callBackEditSubmission={(idSubmission:number, uuidSection:string, submission:any) => callBackEditSubmission!(idSubmission, uuidSection, submission)}
                onPrintSubmission={onPrintSubmission}  />
};

interface ShowSingleSubmissionPatientViewProps extends ShowSubmissionPatientProps {
    uuidInvestigation?: string;
}

export const ShowSingleSubmissionPatientView: React.FC<ShowSingleSubmissionPatientViewProps> = ({ currentSurvey, forceEdit, printMode, callBackEditSubmission, submission, onPrintSubmission, uuidInvestigation }) => {
    const dispatch = useDispatch();

    // SHER-223 Phase 2: addendum modal state lives at the submission level, not
    // per-section, so a single button below the original note opens one form.
    const [addendumOpen, setAddendumOpen] = React.useState(false);

    // Permissions + identity selectors decide which buttons render. The author
    // gate hides the pencil; non-authors get "Add correction"; clinical leads
    // with EDIT_OTHER_AUTHOR_SUBMISSION additionally get "Edit anyway".
    const currentInvestigation = useSelector((state: any) => state.investigations?.currentInvestigation);
    const investigationUuid = uuidInvestigation || currentInvestigation?.uuid;
    const permissions: PERMISSION[] = useSelector((state: any) => state.investigations?.currentInvestigation?.permissions || []);
    const currentResearcherUuid = useSelector((state: any) => state.profile?.info?.uuid);

    const submissionAuthorUuid = submission?.uuidResearcher || submission?.researcher?.uuid;
    const isAuthor = !submissionAuthorUuid || submissionAuthorUuid === currentResearcherUuid;
    const showCorrectionButton = !printMode && !isAuthor;

    // SHER-636: load the amendment history (SubmissionEditHistory) so an edit
    // reads as an attributed amendment. Only rendered when there is history, so
    // never-edited records stay uncluttered.
    const [amendments, setAmendments] = React.useState<AmendmentEntry[]>([]);
    const [historyLoading, setHistoryLoading] = React.useState(false);
    const [historyError, setHistoryError] = React.useState(false);

    React.useEffect(() => {
        let cancelled = false;
        if (!investigationUuid || !submission?.id) {
            setAmendments([]);
            return;
        }
        setHistoryLoading(true);
        setHistoryError(false);
        getSubmissionEditHistoryService(investigationUuid, submission.id)
            .then((data: any) => {
                if (cancelled) return;
                setAmendments(Array.isArray(data?.editHistory) ? data.editHistory : []);
            })
            .catch(() => { if (!cancelled) setHistoryError(true); })
            .finally(() => { if (!cancelled) setHistoryLoading(false); });
        return () => { cancelled = true; };
    }, [investigationUuid, submission?.id]);

    async function handleAddAddendum(body: string) {
        if (!investigationUuid || !submission?.uuidPatient || !submission?.id) return;
        await dispatch(addAddendumPatientAction(
            investigationUuid,
            submission.uuidPatient,
            currentSurvey!.uuid,
            currentSurvey!.name,
            submission.id,
            body,
        ) as any);
    }

    const recordsSection = Object.values(currentSurvey!.sections).sort((a:any,b:any) => a.order - b.order).map((section:any) => {

        const recordsSection = filterRecordsFromSection(submission, section.uuid);

        return(
            <ShowRecordsSection forceEdit={forceEdit} printMode={printMode}
                callBackEditSubmission={(idSubmission:number, uuidSection:string) => {
                    callBackEditSubmission!(idSubmission, uuidSection, submission)
                }}
                records={recordsSection} section={section} uuidResearcher = {submission.uuidResearcher ? submission.uuidResearcher : submission.researcher.uuid}
                idSubmission = {submission.id}
                updatedAt={submission.updatedAt}
                permissions={permissions}
                onPrintSubmission={onPrintSubmission ? () => onPrintSubmission(section.uuid) : undefined}/>
        )
    });
    return (
        <>
            {recordsSection}
            {/* SHER-223 Phase 2: addenda live at submission level, not per-section,
                so they render once below the original record. Visible in printMode
                because addenda are clinical content that must appear in PDFs. */}
            <AddendaList addenda={(submission?.addenda || [])} />
            {!printMode && (amendments.length > 0 || historyLoading || historyError) && (
                <div style={{ marginTop: '1rem' }}>
                    <AmendmentHistory amendments={amendments} loading={historyLoading} error={historyError} />
                </div>
            )}
            {showCorrectionButton && (
                <div style={{ marginTop: '0.5rem' }}>
                    <Button
                        size="small"
                        color="primary"
                        variant="outlined"
                        data-testid="add-correction-button"
                        onClick={() => setAddendumOpen(true)}
                    >
                        <Translate id="hospital.correction-add-button" />
                    </Button>
                </div>
            )}
            <AddAddendumModal
                open={addendumOpen}
                onClose={() => setAddendumOpen(false)}
                onSubmit={handleAddAddendum}
            />
        </>
    );
};

export default ShowSingleSubmissionPatient;
